import { styled } from "@mui/material/styles";
import { Box, Tooltip } from "@mui/material";
import { FC, forwardRef } from "react";
import { useShallow } from "zustand/shallow";
import { TableVirtuoso } from "react-virtuoso";

import { sortMachines, useListBuilderStore } from "stores/list-builder.store";
import { KpisMachinesWithDates } from "interfaces/kpis";
import { SortingIndicator } from "pages/Kpis/KpisDialogs/SortingIndicator";
import { Checkbox } from "pages/Kpis/KpisDialogs/Checkbox";

interface Props {
  type: "available" | "selected";
  disabled?: boolean;
}

export const List: FC<Props> = ({ type, disabled = false }): JSX.Element => {
  const {
    setAvailableChecked,
    filteredAvailable,
    filteredSelected,
    isAvailableChecked,
    isAvailableAllChecked,
    isAvailableIndeterminate,
    removeAvailableChecked,
    setSelectedChecked,
    isSelectedChecked,
    isSelectedAllChecked,
    isSelectedIndeterminate,
    removeSelectedChecked,
    setSortParams,
    sortParams,
    clearChecked
  } = useListBuilderStore(
    useShallow(store => ({
      setAvailableChecked: store.setAvailableChecked,
      filteredAvailable: store.filteredAvailable,
      filteredSelected: store.filteredSelected,
      isAvailableChecked: store.isAvailableChecked,
      isAvailableAllChecked: store.isAvailableAllChecked,
      isAvailableIndeterminate: store.isAvailableIndeterminate,
      removeAvailableChecked: store.removeAvailableChecked,
      setSelectedChecked: store.setSelectedChecked,
      isSelectedChecked: store.isSelectedChecked,
      isSelectedAllChecked: store.isSelectedAllChecked,
      isSelectedIndeterminate: store.isSelectedIndeterminate,
      removeSelectedChecked: store.removeSelectedChecked,
      setSortParams: store.setSortParams,
      sortParams: store.sortParams,
      clearChecked: store.clearChecked
    }))
  );

  let items = type === "available" ? filteredAvailable : filteredSelected;

  const handleChangeAvailable = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KpisMachinesWithDates
  ): void => {
    if (event.target.checked) {
      setAvailableChecked([item]);
    } else {
      removeAvailableChecked([item]);
    }
  };

  const handleChangeSelected = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KpisMachinesWithDates
  ): void => {
    if (event.target.checked) {
      setSelectedChecked([item]);
    } else {
      removeSelectedChecked([item]);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KpisMachinesWithDates
  ): void => {
    if (type === "available") {
      handleChangeAvailable(event, item);
    } else {
      handleChangeSelected(event, item);
    }
  };

  const handleSortTable = (columnName: string): void => {
    setSortParams(columnName);
  };

  const handleSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { checked } = event.target;

    if (type === "available") {
      checked ? setAvailableChecked(filteredAvailable) : clearChecked();
    } else {
      checked ? setSelectedChecked(filteredSelected) : clearChecked();
    }
  };

  const isChecked = (item: KpisMachinesWithDates): boolean => {
    if (type === "available") {
      return isAvailableChecked(item);
    } else {
      return isSelectedChecked(item);
    }
  };

  const isAllChecked = (): boolean => {
    if (type === "available") {
      return isAvailableAllChecked();
    } else {
      return isSelectedAllChecked();
    }
  };

  const isAllIndeterminate = (): boolean => {
    if (type === "available") {
      return isAvailableIndeterminate();
    } else {
      return isSelectedIndeterminate();
    }
  };

  return (
    <TableVirtuoso
      style={{ height: "400px" }}
      data={items.sort((a, b) => sortMachines(a, b, sortParams))}
      components={{
        Table: CustomTable
      }}
      fixedHeaderContent={() => (
        <tr>
          <StyledTh>
            <Checkbox
              id={`select-all--${type}`}
              onChange={handleSelectAll}
              aria-label="Select all"
              disabled={disabled}
              checked={isAllChecked()}
              indeterminate={isAllIndeterminate()}
            />
          </StyledTh>
          <StyledTh>
            <StyledSortButton
              type="button"
              onClick={() => handleSortTable("name")}
              disabled={disabled}
            >
              Name <SortingIndicator name="name" />
            </StyledSortButton>
          </StyledTh>
          <StyledTh>
            <StyledSortButton
              type="button"
              onClick={() => handleSortTable("latest_activity_sortable")}
              disabled={disabled}
            >
              Last activity <SortingIndicator name="latest_activity_sortable" />
            </StyledSortButton>
          </StyledTh>
        </tr>
      )}
      itemContent={(_index, item) => (
        <>
          <StyledTd>
            <input
              type="checkbox"
              id={String(item.id)}
              onChange={event => handleChange(event, item)}
              aria-label={`${item.name} Last activity ${item.latest_activity_human}`}
              disabled={disabled}
              checked={isChecked(item)}
            />
          </StyledTd>
          <StyledTd>
            <Box
              component="label"
              htmlFor={String(item.id)}
              sx={{
                color: disabled ? "text.disabled" : "inherit",
                display: "block",
                height: "100%"
              }}
            >
              {item.name}
            </Box>
          </StyledTd>
          <StyledTd style={{ whiteSpace: "nowrap" }}>
            <Tooltip title={item.overdue_by_human}>
              <Box
                component="label"
                htmlFor={String(item.id)}
                sx={{
                  // prettier-ignore
                  color: disabled
                    ? "text.disabled"
                    : item.is_overdue
                      ? "error.main"
                      : "inherit",
                  display: "block",
                  height: "100%"
                }}
              >
                <time dateTime={item.latest_activity}>
                  {item.latest_activity_human}
                </time>
              </Box>
            </Tooltip>
          </StyledTd>
        </>
      )}
    />
  );
};

const CustomTable = forwardRef<HTMLTableElement, React.PropsWithChildren<{}>>(
  (props, ref) => (
    <table
      {...props}
      ref={ref}
      style={{
        borderCollapse: "collapse",
        tableLayout: "fixed",
        width: "100%"
      }}
    >
      <colgroup>
        <col style={{ width: "4ch" }} />
        <col style={{ width: "auto" }} />
        <col style={{ width: "13ch" }} />
      </colgroup>
      {props.children}
    </table>
  )
);
CustomTable.displayName = "CustomTable";

const StyledTh = styled("th")`
  text-align: left;
  vertical-align: top;
  padding: ${props => props.theme.spacing(0.25)};
  background-color: ${props => props.theme.palette.background.paper};
`;
StyledTh.displayName = "StyledTh";

const StyledTd = styled("td")`
  height: 1px;
  text-align: left;
  vertical-align: top;
  padding: ${props => props.theme.spacing(0.25)};
`;
StyledTd.displayName = "StyledTd";

const StyledSortButton = styled("button")`
  appearance: none;
  border: 0;
  color: ${props => (props.disabled ? "text.disabled" : "inherit")};
  padding: 0;
  cursor: pointer;
  font-weight: inherit;
  background-color: transparent;
  font-size: inherit;
  line-height: inherit;
  display: flex;
  align-items: center;
  width: 100%;
  white-space: nowrap;
`;
StyledSortButton.displayName = "StyledSortButton";
